import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAppSelector } from 'hooks';
import AppointmentActivities from '~appointments/AppointmentActivities';
import classNames from 'classnames';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import { getChangedValues } from '~libs/dataUtils';
import generateYupSchema from '~libs/yupValidation';

import { Button, ButtonProps, Select } from '~components';
import { FieldProps, Fields } from '~components/form-fields';
import FullSizeNotes from '~appointments/components/FullSizeNotes';

import { useGetNoteTypesQuery } from '~appointments/actions/noteTypes';

import { selectCurrentAppointment } from '~appointments/selectors/appointments';

import FormInputGroupParticipants from './FormInputGroupParticipants';
import FormInputGroupRouteMap from './FormInputGroupRouteMap';

interface ComponentProp {
  authTag?: string;
  fields?: any;
  defaultValues?: any;
  overrideValues?: any;
  canCancel?: () => boolean;
  canSave?: () => boolean;
  save?: (values: any, originalValues: any) => void;
  isNew?: boolean;
  isGroup?: boolean;
  showFullNotes?: boolean;
  showRouteMap?: boolean;
  showActivityLogs?: boolean;
  confirmButtonProps?: ButtonProps;
  cancelButtonProps?: ButtonProps;
  isActive?: boolean;
}

const AppointmentForm: React.FC<ComponentProp> = ({
  authTag,
  fields,
  defaultValues = {},
  overrideValues = {},
  canSave,
  canCancel,
  save,
  isNew,
  isGroup = false,
  showFullNotes = false,
  showRouteMap = false,
  showActivityLogs = false,
  confirmButtonProps = {} as ButtonProps,
  cancelButtonProps = {} as ButtonProps,
  isActive = true,
}) => {
  const [invalidFields, setInvalidFields]: [string[], any] = useState([]);

  const appointment = useAppSelector(selectCurrentAppointment);

  const isSplitForm = isGroup || showFullNotes || showRouteMap;

  const fieldDefinitions = useMemo(
    () => Object.entries(fields as FieldProps).map(([key, props]) => ({ key, ...props })),
    [fields],
  );

  const reduceFieldValues = (
    fields: any,
    fillValue = false,
    defaults: Record<string, any> = {},
    overrides: Record<string, any> = {},
  ) =>
    Object.keys(fields).reduce((values, index) => {
      const { key, field, dependencies = [] } = fields[index];
      const id = field ?? key;

      [id, ...dependencies].forEach((dependencyId: string) => {
        const value = overrides[dependencyId];

        if (value !== undefined && value !== null) {
          values[dependencyId] = value;
        } else if (fillValue) values[dependencyId] = '';
      });

      return values;
    }, defaults);

  // Send request to update record, include ID if editing.
  const onSave = async (values: any, originalValues: any) => {
    const valid = invalidFields.length === 0;

    if (isGroup) {
      const { clients, users, ...rest } = values;

      const checkClients = (newClients: any[], originalClients: any[]) => {
        const existing = new Set(originalClients.map((client) => client.client_id));

        return newClients
          ?.filter((client) => !existing.has(client.client_id))
          .map(({ client_id }) => ({ id: client_id }));
      };

      const checkUsers = (newUsers: any[], originalUsers: any[]) => {
        const existing = new Set(originalUsers.map((user) => user.user_id));

        return newUsers?.filter((user) => !existing.has(user.user_id)).map(({ user_id }) => ({ id: user_id }));
      };

      let groupValues;
      if (isNew) {
        groupValues = {
          ...rest,
          clients: clients?.map(({ client_id }: { client_id: number }) => ({ id: client_id })) ?? undefined,
          users: users?.map(({ user_id }: { user_id: number }) => ({ id: user_id })) ?? undefined,
        };
      } else {
        groupValues = {
          ...rest,
          clients: checkClients(clients, originalValues.clients) ?? undefined,
          users: checkUsers(users, originalValues.users) ?? undefined,
        };
      }

      if (valid && save) save(groupValues, originalValues);
      return;
    }
    if (valid && save) save(values, originalValues);
  };

  const initialValues = appointment
    ? { ...appointment, ...defaultValues }
    : reduceFieldValues(fieldDefinitions, true, defaultValues);

  let initialChangedValues = {};
  if (!appointment) {
    initialChangedValues = reduceFieldValues(fieldDefinitions, false, {}, overrideValues);
  }

  /**
   * Dynamic generation of yup validation schema
   */
  const fieldsObject: FieldProps = fieldDefinitions.reduce((acc, field) => {
    const { key, ...fieldProps } = field;
    acc[key] = fieldProps;
    return acc;
  }, {} as FieldProps);

  const validationSchema = generateYupSchema(fieldsObject);

  const handleYupValidation = async (values: any) => {
    try {
      await validationSchema.validate(values, { abortEarly: false });
      setInvalidFields([]);
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const errors = err.inner.map(({ path }) => path).filter((path): path is string => !!path);
        setInvalidFields(errors);
      }
    }
  };

  // Only needed for the "Note Type set on Update" dropdown below (showFullNotes) — skip while this
  // form isn't the active tab so it doesn't fire alongside every other tab's data on page load.
  // refetchOnMountOrArgChange is a duration (not `true`) because this component now stays mounted
  // across Details<->DEX tab switches (see Appointment.tsx's hasMountedDetailsContent) — `skip`
  // toggling on and off looks like a fresh subscription to RTK Query, so `true` would refetch this
  // near-static reference data on every single tab switch instead of only when genuinely stale.
  const { data } = useGetNoteTypesQuery({}, { refetchOnMountOrArgChange: 300, skip: !isActive });
  const noteTypeOptions = data?.map(({ id, name }: { id: number; name: string }) => ({
    value: id,
    label: name,
  }));

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={{ ...initialValues, ...initialChangedValues }}
        validationSchema={validationSchema}
        validate={handleYupValidation}
        validateOnMount={false}
        // Re-validate on change only once the form has hit a validation failure — this
        // clears stale invalid-field highlights as the user fixes them. Before any failure,
        // we stay quiet so the form does not surface errors on untouched fields.
        validateOnChange={invalidFields.length > 0}
        validateOnBlur={false}
        onSubmit={async (values, { setSubmitting }) => {
          onSave(getChangedValues(values, initialValues), initialValues);
          setSubmitting(false);
        }}
      >
        {({ values, setFieldValue, setFieldTouched, touched, isSubmitting }) => {
          const hasInitialized = useRef(false);
          useEffect(() => {
            // Prevent re-triggering if already initialized to avoid overrides wiping out user inputs
            if (hasInitialized.current) {
              return;
            }

            if (fields && overrideValues && Object.keys(initialValues).length > 0) {
              const overlappingKeys = Object.keys(overrideValues).filter((key) =>
                Object.keys(initialValues).includes(key),
              );

              if (overlappingKeys.length > 0) {
                overlappingKeys.forEach((key) => {
                  const value = overrideValues[key];
                  const field = fields.find(
                    (field: { key: any; field: any }) => field.key === key || field.field === key,
                  );

                  if (field && field?.onChange) {
                    const fieldKey = field.field || field.key;

                    if (!touched[fieldKey]) {
                      setFieldValue(fieldKey, value);
                      field.onChange({ ...initialValues, ...overrideValues });
                      hasInitialized.current = true;
                    }
                  }
                });
              }
            }
          }, [overrideValues, fields, initialValues, touched, setFieldValue]);

          const splitClassName = classNames('w-100', { 'split-form-left': isSplitForm });

          return (
            <Form>
              <div className="d-flex w-100">
                <div className={splitClassName}>
                  <Fields
                    authTag={authTag}
                    definitions={fieldDefinitions}
                    row={isNew ? overrideValues : appointment}
                    invalidFields={invalidFields}
                    values={values}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    touched={touched}
                  />
                </div>
                {isSplitForm && (
                  <div className="split-form-right">
                    {showFullNotes && (
                      <>
                        <FullSizeNotes
                          id="notes"
                          name="notes"
                          type="custom"
                          value={values.notes}
                          setFieldValue={setFieldValue}
                          setFieldTouched={setFieldTouched}
                        />
                        <div className="p-2 me-4">
                          <span className="fw-bold ps-1">Note Type set on Update:</span>
                          <Select
                            type="select"
                            name="note_type_id"
                            id="note_type_id"
                            value={values.note_type_id}
                            onChange={(field, value) => {
                              setFieldValue(field, value);
                            }}
                            options={noteTypeOptions}
                          />
                          <div className="ps-2 pe-2 small text-info">
                            * Note will be added to client as selected type, verify current note type in CRM
                          </div>
                        </div>
                      </>
                    )}
                    {isGroup && <FormInputGroupParticipants values={values} setFieldValue={setFieldValue} />}
                    {(isGroup || showRouteMap) && values.id && (
                      <FormInputGroupRouteMap
                        appointmentId={values.id}
                        isNew={isNew}
                        location={values.location_override_id}
                      />
                    )}
                  </div>
                )}
              </div>

              <div>
                {canCancel && <Button size="sm" disabled={isSubmitting || !canCancel()} {...cancelButtonProps} />}
                {canSave && (
                  <Button size="sm" disabled={isSubmitting || !canSave()} type="submit" {...confirmButtonProps} />
                )}
              </div>
            </Form>
          );
        }}
      </Formik>
      {showActivityLogs && <AppointmentActivities className="p-2 ps-4 pe-4 mt-2 mb-5" />}
    </>
  );
};

export default AppointmentForm;
